/** Add to home screen, asked once.
 *
 *  Sits above the capture bar, never over it: the product's primary action does
 *  not get covered by a request. `NOT NOW` settles it permanently — see
 *  `state/install.ts` for why a returning prompt is not an option here.
 */
export function InstallPrompt({
  bottom,
  onInstall,
  onDismiss,
}: {
  /** Height of the fixed chrome this must clear. */
  bottom: number;
  /** Present only when the browser gave us a real prompt to fire. Without it
   *  the sheet explains the manual route instead of offering a button that
   *  would do nothing. */
  onInstall?: () => void;
  onDismiss: () => void;
}) {
  return (
    <div style={{ ...sheet, bottom }}>
      <div style={{ display: 'flex', gap: 'var(--bujo-space-4)', alignItems: 'flex-start' }}>
        <div style={mark}>
          <span style={{ font: 'var(--bujo-fw-regular) 26px/1 var(--bujo-font-mono)', color: 'var(--bujo-accent)' }}>
            ·
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={title}>Install BuJo</div>
          <div style={body}>
            Full screen, no browser chrome, opens on today. Capture works offline and syncs when
            the server is back.
          </div>
        </div>
      </div>
      {/* The manual route is a line of copy, not a flex sibling of the buttons:
          as a sibling it doubled the sheet's height at 320px and covered most of
          the day. */}
      {onInstall ? null : (
        <div style={body}>
          Use your browser's <b>Share → Add to Home Screen</b>. Safari does not let a page ask.
        </div>
      )}
      <div style={{ display: 'flex', gap: 'var(--bujo-space-3)', paddingTop: 'var(--bujo-space-4)' }}>
        {onInstall ? (
          <button type="button" onClick={onInstall} style={primary}>
            ADD TO HOME SCREEN
          </button>
        ) : null}
        <button type="button" onClick={onDismiss} style={{ ...secondary, flex: onInstall ? 'none' : 1 }}>
          NOT NOW
        </button>
      </div>
    </div>
  );
}

const sheet = {
  position: 'fixed' as const,
  left: 0,
  right: 0,
  zIndex: 25,
  background: 'var(--bujo-surface-sheet)',
  borderTop: '1px solid var(--bujo-rule-heavy)',
  padding: 'var(--bujo-space-5) var(--bujo-gutter)',
};
const mark = {
  flex: 'none' as const,
  width: 56,
  height: 56,
  background: 'var(--bujo-surface-page)',
  border: '1px solid var(--bujo-rule-heavy)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const title = {
  font: 'var(--bujo-fw-medium) 15px/1.3 var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
};
const body = {
  paddingTop: 'var(--bujo-space-2)',
  font: 'var(--bujo-fw-regular) 12px/1.6 var(--bujo-font-sans)',
  color: 'var(--bujo-text-dim)',
};
const primary = {
  flex: 1,
  minHeight: 'var(--bujo-tap-min)',
  background: 'var(--bujo-select-ground)',
  border: '1px solid var(--bujo-accent)',
  color: 'var(--bujo-accent)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: 'pointer',
};
const secondary = {
  flex: 'none' as const,
  minHeight: 'var(--bujo-tap-min)',
  padding: '0 var(--bujo-space-4)',
  background: 'none',
  border: '1px solid var(--bujo-rule-hairline)',
  color: 'var(--bujo-text-meta)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: 'pointer',
};
